import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';

/**
 * Traduce el **código de cierre** de un movimiento a lo que el operario necesita leer: por qué
 * terminó así la operación.
 *
 * Por qué es un módulo hermano de `gateCommandOutcome.ts` y no una extensión suya: aquel clasifica
 * la respuesta **síncrona al comando** (`g/r`) y responde «¿salió el comando y qué puedo afirmar de
 * la compuerta?». Éste lee el `close_code` del movimiento y responde «¿por qué terminó así?», para
 * otro consumidor —el snackbar del comando contra el panel de operación—, así que un solo módulo
 * tendría que decidir primero cuál de las dos preguntas le están haciendo. Lo que sí se comparte es
 * la forma: función pura, `i18n` inyectado, y el default conservador que no afirma lo que no sabe.
 *
 * Los espacios de códigos no son disjuntos. `close_code` trae el cierre del ciclo de vida (`g/o`:
 * GO01/GO02, GO20-GO2B, GO99), los `Q*` del carril encolado y también las respuestas `g/r` ≠ GO00
 * que el drenaje de la cola guarda como cierre (hoy GO03, GO09, GO10-GO1A y GO27). GO27 es ambiguo:
 * aborto por `g/o` o rechazo sin movimiento por el drenaje, por eso su texto no afirma ninguno.
 *
 * El discriminante importa: un atasco (GO23), un timeout (GO25) y una falla del driver (GO29)
 * llegan los tres como `FINALIZED_WITH_ERROR`. Sin esto el operario ve «finalizó con error» y no
 * sabe cuál de los tres tiene enfrente ni qué hacer al respecto.
 */
export interface GateCloseDescription {
  /** El código tal como lo informó el equipo, para reportarlo. */
  code: string;
  title: string;
  /** Qué puede hacer el operario, en minúscula y sin punto; ausente si no hay nada que hacer. */
  action?: string;
}

/** Forma de un código de operación del firmware: dos caracteres tras `GO` (`GO2A` incluido). */
const CLOSE_CODE = /^GO[0-9A-Z]{2}$/;

type CloseCodeEntry = {
  title: (i18n: I18n) => string;
  action?: (i18n: I18n) => string;
};

const CLOSE_CODES: Record<string, CloseCodeEntry> = {
  GO01: {
    title: (i18n) => t(i18n)`Operación completada`,
  },
  GO02: {
    title: (i18n) => t(i18n)`Fin de carrera alcanzado`,
  },
  // Respuestas `g/r` que el drenaje de la cola guarda como cierre: ninguna movió la compuerta.
  GO03: {
    title: (i18n) => t(i18n)`Compuerta ya en la posición solicitada`,
  },
  GO09: {
    title: (i18n) => t(i18n)`Solicitud rechazada por vencida`,
  },
  GO10: {
    title: (i18n) => t(i18n)`Solicitud rechazada: posición fuera de rango`,
  },
  GO11: {
    title: (i18n) => t(i18n)`Solicitud rechazada: automatizador en modo manual`,
  },
  GO12: {
    title: (i18n) => t(i18n)`Solicitud rechazada: parada de emergencia activa`,
    action: (i18n) => t(i18n)`desactivar la parada de emergencia en terreno`,
  },
  GO13: {
    title: (i18n) => t(i18n)`Solicitud rechazada: fin de carrera en el sentido solicitado`,
  },
  GO14: {
    title: (i18n) => t(i18n)`Solicitud rechazada: compuerta ocupada`,
  },
  GO15: {
    title: (i18n) => t(i18n)`Solicitud rechazada: la posición de la compuerta cambió`,
    action: (i18n) => t(i18n)`volver a solicitar la operación`,
  },
  GO16: {
    title: (i18n) => t(i18n)`Solicitud rechazada: entradas de seguridad ilegibles`,
  },
  GO17: {
    title: (i18n) => t(i18n)`Solicitud rechazada: parámetros inválidos`,
  },
  GO18: {
    title: (i18n) => t(i18n)`Solicitud rechazada: compuerta no reconocida por el equipo`,
  },
  GO19: {
    title: (i18n) => t(i18n)`Solicitud rechazada: automatizador no configurado`,
  },
  GO1A: {
    title: (i18n) => t(i18n)`Solicitud rechazada: posición actual desconocida`,
  },
  GO20: {
    // No se afirma el origen: un GO20 histórico o de firmware previo puede ser un stop local o un
    // apagado, no solo una orden remota.
    title: (i18n) => t(i18n)`Operación detenida`,
  },
  GO21: {
    title: (i18n) => t(i18n)`Operación detenida por parada de emergencia`,
    action: (i18n) => t(i18n)`desactivar la parada de emergencia en terreno`,
  },
  GO22: {
    title: (i18n) => t(i18n)`Operación interrumpida por paso a modo manual`,
  },
  GO23: {
    title: (i18n) => t(i18n)`Operación detenida por atasco`,
    action: (i18n) => t(i18n)`revisar obstrucciones en el mecanismo`,
  },
  GO24: {
    title: (i18n) => t(i18n)`Operación detenida por error de lectura de posición`,
  },
  GO25: {
    title: (i18n) => t(i18n)`Operación detenida por tiempo máximo excedido`,
  },
  GO26: {
    title: (i18n) => t(i18n)`Operación detenida por movimiento en sentido inverso`,
    action: (i18n) => t(i18n)`revisar el conexionado del actuador`,
  },
  GO27: {
    // Neutro a propósito: el mismo código llega como aborto (`g/o`) o como rechazo del drenaje.
    title: (i18n) => t(i18n)`Operación finalizada por fin de carrera de seguridad`,
    action: (i18n) => t(i18n)`revisar el fin de carrera en terreno`,
  },
  GO28: {
    title: (i18n) => t(i18n)`Operación interrumpida por apagado o reinicio del equipo`,
    action: (i18n) => t(i18n)`verificar la posición antes de volver a operar`,
  },
  GO29: {
    title: (i18n) => t(i18n)`Operación detenida por falla del actuador`,
    action: (i18n) => t(i18n)`revisar el actuador en terreno`,
  },
  GO2A: {
    title: (i18n) => t(i18n)`Operación detenida por entradas de seguridad ilegibles`,
  },
  GO2B: {
    title: (i18n) => t(i18n)`Operación detenida desde el equipo`,
  },
  // Cierres del carril de solicitudes encoladas (SGMO-14). SGMO los escribe en la misma columna
  // `close_code` con prefijo `Q` para no chocar con el catálogo `GO**` del firmware. Ninguno de
  // los tres describe un movimiento del equipo: la solicitud se cerró antes (o en lugar) de eso.
  QCANCEL: {
    title: (i18n) => t(i18n)`Solicitud en cola cancelada`,
  },
  QEXPIRED: {
    title: (i18n) => t(i18n)`Solicitud en cola vencida`,
  },
  QDUPMID: {
    title: (i18n) => t(i18n)`Solicitud en cola entregada`,
  },
  GO99: {
    title: (i18n) => t(i18n)`Operación finalizada por una causa desconocida`,
  },
};

/** Normaliza lo que llegue del payload: solo un código con la forma del contrato cuenta. */
const readCloseCode = (code: unknown): string | null => {
  if (typeof code !== 'string') return null;
  const normalized = code.trim().toUpperCase();
  return normalized.length > 0 ? normalized : null;
};

/**
 * Descripción operacional del cierre, o `null` cuando el mensaje no trae código —ahí el consumidor
 * se queda con el texto genérico de su tipo, que es todo lo que se sabe.
 *
 * Un código con forma válida que esta versión del frontend no conoce **no** se descarta: se
 * devuelve con un título genérico y el código a la vista, para que el operario tenga algo que
 * reportar.
 */
export const describeGateCloseCode = (
  code: unknown,
  i18n: I18n,
): GateCloseDescription | null => {
  const normalized = readCloseCode(code);
  if (normalized == null) return null;

  const entry = CLOSE_CODES[normalized];
  if (entry) {
    return {
      code: normalized,
      title: entry.title(i18n),
      action: entry.action?.(i18n),
    };
  }

  if (!CLOSE_CODE.test(normalized)) return null;

  return {
    code: normalized,
    title: t(i18n)`Operación finalizada con un código no reconocido`,
  };
};
